import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateTesterDialogComponent } from '../create-tester-dialog/create-tester-dialog.component';
import { EMPTY, Observable, merge, of } from 'rxjs';
import { Tester } from 'src/app/model/tester';
import { switchMap, tap } from 'rxjs/operators';
import { SnackService } from 'src/app/services/snack.service';
import { UsersService } from 'src/app/services/users.service';
import { Constants } from 'src/app/utils/constants';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { GeneralItemDetailDialogComponent } from 'src/app/shared-components/general-item-detail-dialog/general-item-detail-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { DialogsService } from 'src/app/services/dialogs.service';
import { Testbed } from 'src/app/model/testbed';


@Component({
  selector: 'aw-list-testers',
  templateUrl: './list-testers.component.html',
  styleUrls: ['./list-testers.component.scss']
})
export class ListTestersComponent implements OnInit, AfterViewInit {

  public displayedColumns = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'company',
    'enabled',
    'action'
  ];

  private allTesters$!: Observable<Tester[]>;
  disabled = false;
  disabledItemId: number[] = [];
  loading = false;
  entities = Constants.entities;
  roles = Constants.roles;
  isUserEnabled?= false;
  @ViewChild('paginatorTesters') paginatorUsers!: MatPaginator;
  @Input() companyId?: number;
  @Input() embeddedLayout = false;
  @Input() testbed?: Testbed; 
  expandedTestbeds?: Tester;
  dataSource!: MatTableDataSource<Tester>;
  loggedUser: any;

  constructor (
    public dialog: MatDialog,
    private snackService: SnackService,
    private usersService: UsersService,
    private dialogsService: DialogsService,
    private authService: AuthService
  ){}

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
    if (!this.companyId && (this.loggedUser.role === this.roles.customer || this.loggedUser.role === this.roles.tester))
      this.companyId = this.loggedUser.company.id;
    if (this.loggedUser.role === this.roles.tester)
      this.displayedColumns =  [
        'firstName',
        'lastName',
        'email',
        'phone',
        'company',
        'action'
      ];
  }

  ngAfterViewInit(): void {
    this.allTesters$=
      of(true).pipe(
        tap(() => this.loading = true),
        switchMap(() => {
          if (this.companyId && !this.embeddedLayout)
            return this.usersService.getAllTestersByCompany(this.companyId);
          else if (this.testbed && this.embeddedLayout) {
            return this.usersService.getAllTestersByTestbed(this.testbed?.id);
          }
          else
            return this.usersService.getAllTesters();
        }),
        tap((x) => {
          this.dataSource = new MatTableDataSource<Tester>(x);
          this.dataSource.paginator = this.paginatorUsers;
          this.loading = false;
        })
      );
    this.allTesters$.subscribe();
  }

  applyFilters(event: string): void {

    this.dataSource.filter = event.trim().toLowerCase();
  }

  createTester(): void {
    this.disabled = true;
    this.dialog
      .open(CreateTesterDialogComponent, { data: new Tester() })
      .afterClosed()
      .pipe(switchMap(value => value ? this.allTesters$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        });
  }

  doSortUsers(event: Sort): void {
    this.dataSource.paginator?.firstPage();
    this.dataSource.data = this.dataSource.data.sort((a, b) => {


      if (event.active === 'id') {
        if (event.direction === 'asc') {
          return ('' + b.id).localeCompare('' + a.id);
        } else {
          return ('' + a.id).localeCompare('' + b.id);
        }
      }

      if (event.active === 'firstName') {
        if (event.direction === 'asc') {
          return ('' + b.firstName).localeCompare('' + a.firstName);
        } else {
          return ('' + a.firstName).localeCompare('' + b.firstName);
        }
      }

      if (event.active === 'lastName') {
        if (event.direction === 'asc') {
          return ('' + b.lastName).localeCompare('' + a.lastName);
        } else {
          return ('' + a.lastName).localeCompare('' + b.lastName);
        }
      }

      if (event.active === 'email') {
        if (event.direction === 'asc') {
          return ('' + b.email).localeCompare('' + a.email);
        } else {
          return ('' + a.email).localeCompare('' + b.email);
        }
      }

      if (event.active === 'phone') {
        if (event.direction === 'asc') {
          return ('' + b.phone).localeCompare('' + a.phone);
        } else {
          return ('' + a.phone).localeCompare('' + b.phone);
        }
      }

      if (event.active === 'company') {
        if (event.direction === 'asc') {
          return ('' + b.company?.companyName).localeCompare('' + a.company?.companyName);
        } else {
          return ('' + a.company?.companyName).localeCompare('' + b.company?.companyName);
        }
      }

      return -1;


    }
    );
  }

  details(user: Tester): void {
    this.dialog.open(GeneralItemDetailDialogComponent, { data: user });
  }

  deleteUser(tester: Tester): void {
    this.disabled = true;
    const id = tester.id ?? -1;
    this.disabledItemId.push(id);
    this.dialogsService
      .confirm('Delete tester', `Are you sure you want to delete the tester "${tester.firstName} ${tester.lastName}"?`)
      .pipe(
        switchMap((value) => value ?
          this.usersService.deleteTester(tester.id) :
          EMPTY),
        switchMap(() => this.allTesters$)
      )
      .subscribe(() => {
        this.disabled = false;
        this.snackService.success('Tester deleted!');
        this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
      }, (error) => {
        this.snackService.error(error.message);
        this.disabled = false;
        this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
      },
        () => {
          this.disabled = false;
        });
  }

  expandTestbeds(element: Tester) {

    this.expandedTestbeds != element ?
      this.expandedTestbeds = element :
      this.expandedTestbeds = undefined;

  }

  removeTesterFromTestbed(tester: Tester) {
    this.disabled = true;
    this.dialogsService
      .confirm('Remove tester', `Are you sure you want to remove the tester from this testbed?`)
      .pipe(
        switchMap((value) => value ?
          this.usersService.removeTesterFromTestbed(this.testbed?.id, tester.id) :
          EMPTY),
        switchMap(() => this.allTesters$)
      )
      .subscribe(() => {
        this.disabled = false;
        this.snackService.success('Tester removed!');
      }, (error) => {
        this.snackService.error(error.message);
        this.disabled = false;
      },
        () => {
          this.disabled = false;
        });

  }


}
